import { useEffect, useState, type FC } from "react";
import { PageOptions } from "../../types/page";
import { useTranslation } from "../../contexts/useTranslation";
import Navbar from "../../components/Navbar";
import ArrowLeftIcon from "../../components/icons/ArrowLeftIcon";
import { MessageThread } from "../../core/types";
import Loader from "../ConversationPage/components/Loader";
import ChatList from "./components/ChatList";
import { useCore } from "../../contexts/useCore";
import { getErrorStatus } from "../../core/utils/getErrorStatus";
import { Slot, Templated } from "../../templating";

interface ChatHistoryPageProps {
  navigate: (pageOptions: PageOptions) => void;
  isFullscreen: boolean;
}

const ChatHistoryPage: FC<ChatHistoryPageProps> = ({ navigate, isFullscreen }) => {
  const { translate } = useTranslation();
  const core = useCore();
  const [isLoading, setIsLoading] = useState(true);
  const [messageThreads, setMessageThreads] = useState<MessageThread[]>([]);

  const handleItemClick = (messageThread: MessageThread): void => {
    navigate({ page: { type: "conversation", params: { messageThreadId: messageThread.id.toString() } } });
  };

  useEffect(() => {
    let cancelled = false;

    const fetchMessageThreads = async (): Promise<void> => {
      try {
        const threads = await core.getThreadList();
        if (cancelled) return;
        setMessageThreads(threads);
        setIsLoading(false);
      } catch (err) {
        if (cancelled) return;
        // 499 (Nginx "Client Closed Request") = the in-flight request was
        // cancelled (e.g. core.destroy() aborting its signal while this page
        // is still mounted). Don't route the user to the error page for a
        // cancellation they may not have initiated themselves — but do clear
        // the spinner so a subsequent remount/interaction doesn't show a
        // stuck loading state. getErrorStatus normalizes both cause shapes
        // used across the codebase (cause: number and cause: { status }).
        const status = getErrorStatus(err);
        if (status === 499) {
          setIsLoading(false);
          return;
        }
        navigate({ page: { type: "errorPage" } });
      }
    };

    void fetchMessageThreads();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Templated id="lastbot-chat-history-page">
      <>
        <Slot name="navbar">
          <Navbar isFullscreen={isFullscreen}>
            <button
              type="button"
              onClick={() => navigate({ page: { type: "landing" } })}
              className="-ml-1 text-lastbot-icon-muted cursor-pointer"
            >
              <ArrowLeftIcon />
            </button>
          </Navbar>
        </Slot>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div className="flex-1 flex flex-col p-4 overflow-y-auto">
              {messageThreads.length === 0 ? (
                <Slot name="thread-list-empty">
                  <ChatList messageThreads={messageThreads} onClick={handleItemClick} />
                </Slot>
              ) : (
                <Slot name="thread-list">
                  <ChatList messageThreads={messageThreads} onClick={handleItemClick} />
                </Slot>
              )}
            </div>
            <div className="px-4 pb-4 pt-4 text-center text-lastbot-on-surface border-t">
              <div className="text-sm">
                <Slot name="form-title">{translate("ready_to_chat")}</Slot>
              </div>
              <button
                type="button"
                onClick={() => navigate({ page: { type: "landing" } })}
                className="text-xs mt-1 underline cursor-pointer"
                data-testid="start-new-conversation"
              >
                {translate("start_new_conversation")}
              </button>
            </div>
          </>
        )}
      </>
    </Templated>
  );
};

export default ChatHistoryPage;
